<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useListenConfigStore } from "@/hooks/useListenConfigStore";
import { imageConfig } from "@/views/listening/components/ThemeTag/imageConfig";


// Stores & Refs
const listenStore = useListenConfigStore();
const showAccent = ref(false);
const currentAccent = ref("default");
const accentRef = ref();

// Computed
const theme = computed(() => listenStore.theme);
const imageMap = computed(() => imageConfig[theme.value]);

// Constants
const ACCENT_LIST = [
  { label: "本地发音", value: "default" },
  // { label: "美式发音", value: "USA" },
  // { label: "英式发音", value: "UK" },
  // { label: "澳式发音", value: "AUS" },
];

// Methods
const changeAccent = (value) => {
  currentAccent.value = value;
  showAccent.value = false; // Close dropdown after selection
};

const handleClickOutside = (e) => {
  if(showAccent.value){
    let isSelf = accentRef.value.contains(e.target)
    if(!isSelf && e.target.alt !== 'accent'){
      showAccent.value = false
    }
  }
};

const toggleAccent = () => {
  showAccent.value = !showAccent.value;
};

// Lifecycle
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div :class="theme" class="accent-container">
    <img
      :src="imageMap.icon_accent"
      alt="accent"
      class="accent-trigger"
      @click="toggleAccent"
    />

    <transition name="accent-dropdown">
      <div v-if="showAccent" class="accent-box" ref="accentRef">
        <div
          v-for="item in ACCENT_LIST"
          :key="item.value"
          class="accent-box_item"
          :class="{ active: currentAccent === item.value }"
          @click="changeAccent(item.value)"
        >
          <div v-if="item.value === 'default'" class="accent-default">
            原
          </div>
          <img
            v-else-if="item.value === 'USA'"
            src="@/assets/listening/v-USA.svg"
            alt="美式发音"
          />
          <img
            v-else-if="item.value === 'UK'"
            src="@/assets/listening/v-UK.svg"
            alt="英式发音"
          />
          <img
            v-else-if="item.value === 'AUS'"
            src="@/assets/listening/v-Aussie.svg"
            alt="澳式发音"
          />
          <span class="accent-label">{{ item.label }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="less">
.accent-container {
  position: relative;
  display: inline-block;
}

.accent-trigger {
  width: 1.28rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.accent-box {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 0.5rem;
  background: white;
  padding: 0.5rem 0.63rem;
  border-radius: 0.63rem;
  width: 7rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0.38rem;

  &_item {
    display: flex;
    align-items: center;
    color: #333;
    font-size: 0.76rem;
    background-color: #f2f3f6;
    border-radius: 0.38rem;
    padding: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background-color: #e8e9ec;
      transform: translateY(-1px);
    }

    &.active {
      border-color: var(--accent-active-color);
      color: var(--accent-active-color);
      background-color: rgba(var(--accent-active-color-rgb), 0.1);
    }
  }
}

.accent-default {
  width: 1.02rem;
  height: 1.02rem;
  border-radius: 50%;
  color: white;
  font-size: 0.57rem;
  background-color: var(--accent-active-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.2rem;
  flex-shrink: 0;
}

.accent-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Dropdown transition
.accent-dropdown-enter-active,
.accent-dropdown-leave-active {
  transition: all 0.2s ease;
  transform-origin: top center;
}

.accent-dropdown-enter-from {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

.accent-dropdown-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}
</style>